import { Link, type LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { cn, FOCUS_RING, ProgressBar } from '@/components/base'
import { EmptyState } from '@/components/EmptyState'

type RankedItem = {
  key: string
  label: ReactNode
  value: number
  /** Where the entry lives, for one that has a screen of its own. */
  to?: LinkProps
}

/**
 * Entries with a figure each and a bar against the largest — which entries
 * carry the most, and by how much.
 *
 * Drawn in the order given: the caller knows whether the list ranks by the
 * figure or by something the figure only measures. The bars compare within
 * the list only, so it answers "which of these", not "how much of the whole".
 * `format` writes the figure; a count by default.
 */
export function RankedList({
  items,
  format = String,
  emptyTitle,
}: {
  items: RankedItem[]
  format?: (value: number) => ReactNode
  /** What stands in the list's place while it has no entries, as `DataTable`
   *  names it. */
  emptyTitle: string
}) {
  if (items.length === 0)
    return <EmptyState layout="inline" title={emptyTitle} />

  const max = Math.max(...items.map(item => item.value), 1)

  return (
    <ul className="flex flex-col gap-3">
      {items.map(item => (
        <li key={item.key} className="flex flex-col gap-1">
          <span className="flex flex-row items-baseline justify-between gap-3 text-[13px]">
            {item.to ? (
              <Link
                {...item.to}
                className={cn(
                  'min-w-0 truncate rounded-sm text-foreground hover:underline',
                  FOCUS_RING,
                )}
              >
                {item.label}
              </Link>
            ) : (
              <span className="min-w-0 truncate text-foreground">
                {item.label}
              </span>
            )}
            <span className="shrink-0 font-medium tabular-nums text-foreground">
              {format(item.value)}
            </span>
          </span>
          <ProgressBar size="sm" percentage={(item.value / max) * 100} />
        </li>
      ))}
    </ul>
  )
}
